import "./CompanyPromotions.css";

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import axios from "axios";
import { toast } from "react-toastify";

import { usePermissions } from "./PermissionContext";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const EMPTY_FORM = {
  name: "",
  description: "",
  discount_type: "percentage",
  discount_value: "",
  minimum_quantity: "1",
  apply_to_all_products: false,
  is_active: true,
  valid_from: "",
  valid_until: "",
  product_ids: [],
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .join(". ");
  }

  return "Ocurrió un error. Intenta nuevamente.";
};

const getResponseItems = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.items ?? data?.data ?? [];
};

const formatDateForInput = (value) => {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
};

const getPromotionStatus = (promotion) => {
  if (!promotion.is_active) {
    return {
      label: "Inactiva",
      className: "promotion-status-inactive",
    };
  }

  const today = new Date().toISOString().slice(0, 10);

  if (
    promotion.valid_from &&
    promotion.valid_from > today
  ) {
    return {
      label: "Programada",
      className: "promotion-status-scheduled",
    };
  }

  if (
    promotion.valid_until &&
    promotion.valid_until < today
  ) {
    return {
      label: "Vencida",
      className: "promotion-status-expired",
    };
  }

  return {
    label: "Vigente",
    className: "promotion-status-active",
  };
};

const formatPromotionDiscount = (promotion) => {
  if (promotion.discount_type === "percentage") {
    return `${Number(promotion.discount_value)}%`;
  }

  return money(promotion.discount_value);
};

function CompanyPromotions() {
  const { can } = usePermissions();

  const canManagePromotions = can("products.manage");

  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedPromotionId, setSelectedPromotionId] =
    useState(null);

  const [selectedPromotion, setSelectedPromotion] =
    useState(null);

  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPromotions = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/promotions/me`,
      {
        headers: getHeaders(),
      }
    );

    const items = getResponseItems(response.data);

    setPromotions(items);

    setSelectedPromotionId((currentId) => {
      const currentStillExists = items.some(
        (promotion) =>
          Number(promotion.id) === Number(currentId)
      );

      if (currentStillExists) {
        return currentId;
      }

      return items[0]?.id ?? null;
    });

    return items;
  }, []);

  const loadProducts = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/inventory/me`,
      {
        headers: getHeaders(),
        params: {
          limit: 100,
          offset: 0,
        },
      }
    );

    setProducts(getResponseItems(response.data));
  }, []);

  const loadInitialData = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadPromotions(),
        loadProducts(),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [loadProducts, loadPromotions]);

  const loadPromotionDetail = useCallback(async () => {
    if (!selectedPromotionId) {
      setSelectedPromotion(null);
      return;
    }

    setLoadingDetail(true);

    try {
      const response = await axios.get(
        `${API_URL}/promotions/me/${selectedPromotionId}`,
        {
          headers: getHeaders(),
        }
      );

      const promotion = response.data;

      setSelectedPromotion(promotion);

      setForm({
        name: promotion.name || "",
        description: promotion.description || "",
        discount_type:
          promotion.discount_type || "percentage",
        discount_value: String(
          promotion.discount_value ?? ""
        ),
        minimum_quantity: String(
          promotion.minimum_quantity ?? "1"
        ),
        apply_to_all_products:
          Boolean(promotion.apply_to_all_products),
        is_active: Boolean(promotion.is_active),
        valid_from: formatDateForInput(
          promotion.valid_from
        ),
        valid_until: formatDateForInput(
          promotion.valid_until
        ),
        product_ids: (promotion.items || []).map(
          (item) => Number(item.product_id)
        ),
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedPromotionId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadPromotionDetail();
  }, [loadPromotionDetail]);

  const handleFormChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((currentForm) => ({
      ...currentForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleProduct = (productId) => {
    const normalizedProductId = Number(productId);

    setForm((currentForm) => {
      const isSelected =
        currentForm.product_ids.includes(
          normalizedProductId
        );

      return {
        ...currentForm,
        product_ids: isSelected
          ? currentForm.product_ids.filter(
            (id) => id !== normalizedProductId
          )
          : [
            ...currentForm.product_ids,
            normalizedProductId,
          ],
      };
    });
  };

  const startNewPromotion = () => {
    setSelectedPromotionId(null);
    setSelectedPromotion(null);
    setForm(EMPTY_FORM);
  };

  const selectPromotion = (promotionId) => {
    setSelectedPromotionId(promotionId);
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    discount_type: form.discount_type,
    discount_value: Number(form.discount_value),
    minimum_quantity: Number(form.minimum_quantity),
    apply_to_all_products: form.apply_to_all_products,
    is_active: form.is_active,
    valid_from: form.valid_from || null,
    valid_until: form.valid_until || null,
  });

  const validateForm = () => {
    if (!form.name.trim()) {
      toast.error("Ingresa el nombre de la promoción.");
      return false;
    }

    if (Number(form.discount_value) <= 0) {
      toast.error("Ingresa un descuento mayor que cero.");
      return false;
    }

    if (Number(form.minimum_quantity) <= 0) {
      toast.error(
        "La cantidad mínima debe ser mayor que cero."
      );
      return false;
    }

    if (
      form.discount_type === "percentage" &&
      Number(form.discount_value) > 100
    ) {
      toast.error(
        "El descuento porcentual no puede superar 100%."
      );
      return false;
    }

    if (
      form.valid_from &&
      form.valid_until &&
      form.valid_until < form.valid_from
    ) {
      toast.error(
        "La fecha final no puede ser anterior a la inicial."
      );
      return false;
    }

    if (
      !form.apply_to_all_products &&
      form.product_ids.length === 0
    ) {
      toast.error(
        "Selecciona productos o aplica la promoción a todo el catálogo."
      );
      return false;
    }

    return true;
  };

  const savePromotion = async (event) => {
    event.preventDefault();

    if (!canManagePromotions) {
      toast.error(
        "No tienes permiso para administrar promociones."
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const payload = buildPayload();

      if (!selectedPromotionId) {
        const response = await axios.post(
          `${API_URL}/promotions/me`,
          {
            ...payload,
            product_ids: form.apply_to_all_products
              ? []
              : form.product_ids,
          },
          {
            headers: getHeaders(),
          }
        );

        setSelectedPromotionId(response.data.id);

        toast.success(
          "Promoción creada correctamente."
        );
      } else {
        /*
          Primero se asignan productos específicos.
          El endpoint cambia la promoción desde “todos”
          a “productos seleccionados” cuando corresponde.
        */
        if (!form.apply_to_all_products) {
          await axios.put(
            `${API_URL}/promotions/me/${selectedPromotionId}/products`,
            {
              product_ids: form.product_ids,
            },
            {
              headers: getHeaders(),
            }
          );
        }

        await axios.patch(
          `${API_URL}/promotions/me/${selectedPromotionId}`,
          payload,
          {
            headers: getHeaders(),
          }
        );

        toast.success(
          "Promoción actualizada correctamente."
        );
      }

      await loadPromotions();
      await loadPromotionDetail();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const togglePromotionStatus = async (promotion) => {
    if (!canManagePromotions) {
      return;
    }

    try {
      await axios.patch(
        `${API_URL}/promotions/me/${promotion.id}`,
        {
          is_active: !promotion.is_active,
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        promotion.is_active
          ? "Promoción desactivada."
          : "Promoción activada."
      );

      await loadPromotions();
      await loadPromotionDetail();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className="company-promotions">
        <p className="promotions-loading">
          Cargando promociones…
        </p>
      </div>
    );
  }

  return (
    <div className="company-promotions">
      <section className="promotions-header">
        <div>
          <p className="promotions-kicker">
            COMERCIAL Y VENTAS
          </p>

          <h1>Promociones</h1>

          <p>
            Define descuentos automáticos por producto,
            cantidad y vigencia.
          </p>
        </div>

        {canManagePromotions && (
          <button
            type="button"
            className="btn-create-promotion"
            onClick={startNewPromotion}
          >
            + Nueva promoción
          </button>
        )}
      </section>

      <main className="promotions-layout">
        <aside className="promotions-list-panel">
          <div className="promotions-list-heading">
            <h2>Promociones disponibles</h2>
            <span>{promotions.length}</span>
          </div>

          {promotions.length === 0 ? (
            <div className="promotions-empty">
              <p>Aún no hay promociones creadas.</p>
            </div>
          ) : (
            <div className="promotions-list">
              {promotions.map((promotion) => {
                const promotionStatus =
                  getPromotionStatus(promotion);

                return (
                  <button
                    type="button"
                    key={promotion.id}
                    className={`promotion-list-item ${
                      Number(selectedPromotionId) ===
                      Number(promotion.id)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() =>
                      selectPromotion(promotion.id)
                    }
                  >
                    <div>
                      <strong>{promotion.name}</strong>

                      <small>
                        {promotion.apply_to_all_products
                          ? "Todo el catálogo"
                          : `${promotion.total_products} producto(s)`}
                      </small>
                    </div>

                    <div className="promotion-list-meta">
                      <b>
                        {formatPromotionDiscount(
                          promotion
                        )}
                      </b>

                      <span
                        className={
                          promotionStatus.className
                        }
                      >
                        {promotionStatus.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        <section className="promotion-editor-panel">
          <div className="promotion-editor-heading">
            <div>
              <p className="promotions-kicker">
                {selectedPromotion
                  ? "EDITAR PROMOCIÓN"
                  : "NUEVA PROMOCIÓN"}
              </p>

              <h2>
                {selectedPromotion
                  ? selectedPromotion.name
                  : "Configura un descuento"}
              </h2>
            </div>

            {selectedPromotion && canManagePromotions && (
              <button
                type="button"
                className={
                  selectedPromotion.is_active
                    ? "btn-deactivate-promotion"
                    : "btn-activate-promotion"
                }
                onClick={() =>
                  togglePromotionStatus(
                    selectedPromotion
                  )
                }
              >
                {selectedPromotion.is_active
                  ? "Desactivar"
                  : "Activar"}
              </button>
            )}
          </div>

          {loadingDetail ? (
            <p className="promotions-loading">
              Cargando promoción…
            </p>
          ) : (
            <form
              className="promotion-form"
              onSubmit={savePromotion}
            >
              <div className="promotion-form-grid">
                <label className="promotion-field promotion-field-wide">
                  Nombre de la promoción

                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    maxLength="150"
                    placeholder="Ej. Oferta Arverja 10%"
                    disabled={!canManagePromotions}
                  />
                </label>

                <label className="promotion-field promotion-field-wide">
                  Descripción

                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleFormChange}
                    maxLength="500"
                    rows="3"
                    placeholder="Detalle interno de la promoción"
                    disabled={!canManagePromotions}
                  />
                </label>

                <label className="promotion-field">
                  Tipo de descuento

                  <select
                    name="discount_type"
                    value={form.discount_type}
                    onChange={handleFormChange}
                    disabled={!canManagePromotions}
                  >
                    <option value="percentage">
                      Porcentaje
                    </option>

                    <option value="fixed_amount">
                      Valor fijo
                    </option>
                  </select>
                </label>

                <label className="promotion-field">
                  {form.discount_type === "percentage"
                    ? "Porcentaje de descuento"
                    : "Valor del descuento"}

                  <input
                    type="number"
                    name="discount_value"
                    min="0.01"
                    max={
                      form.discount_type === "percentage"
                        ? "100"
                        : undefined
                    }
                    step="0.01"
                    value={form.discount_value}
                    onChange={handleFormChange}
                    placeholder={
                      form.discount_type === "percentage"
                        ? "10"
                        : "1000"
                    }
                    disabled={!canManagePromotions}
                  />
                </label>

                <label className="promotion-field">
                  Cantidad mínima

                  <input
                    type="number"
                    name="minimum_quantity"
                    min="1"
                    step="1"
                    value={form.minimum_quantity}
                    onChange={handleFormChange}
                    disabled={!canManagePromotions}
                  />
                </label>

                <label className="promotion-field">
                  Estado

                  <select
                    name="is_active"
                    value={String(form.is_active)}
                    onChange={(event) =>
                      setForm((currentForm) => ({
                        ...currentForm,
                        is_active:
                          event.target.value === "true",
                      }))
                    }
                    disabled={!canManagePromotions}
                  >
                    <option value="true">Activa</option>
                    <option value="false">Inactiva</option>
                  </select>
                </label>

                <label className="promotion-field">
                  Vigente desde

                  <input
                    type="date"
                    name="valid_from"
                    value={form.valid_from}
                    onChange={handleFormChange}
                    disabled={!canManagePromotions}
                  />
                </label>

                <label className="promotion-field">
                  Vigente hasta

                  <input
                    type="date"
                    name="valid_until"
                    value={form.valid_until}
                    onChange={handleFormChange}
                    disabled={!canManagePromotions}
                  />
                </label>
              </div>

              <section className="promotion-products-section">
                <label className="promotion-checkbox-row">
                  <input
                    type="checkbox"
                    name="apply_to_all_products"
                    checked={form.apply_to_all_products}
                    onChange={handleFormChange}
                    disabled={!canManagePromotions}
                  />

                  <span>
                    Aplicar esta promoción a todos los productos
                    activos.
                  </span>
                </label>

                {!form.apply_to_all_products && (
                  <>
                    <div className="promotion-products-heading">
                      <div>
                        <h3>Productos aplicables</h3>
                        <p>
                          Seleccionados:{" "}
                          {form.product_ids.length}
                        </p>
                      </div>
                    </div>

                    <div className="promotion-products-list">
                      {products.map((product) => {
                        const productId = Number(
                          product.id ?? product.product_id
                        );

                        const selected =
                          form.product_ids.includes(
                            productId
                          );

                        return (
                          <label
                            className="promotion-product-row"
                            key={productId}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() =>
                                toggleProduct(productId)
                              }
                              disabled={!canManagePromotions}
                            />

                            <span className="promotion-product-info">
                              <strong>
                                {product.name ||
                                  product.product_name}
                              </strong>

                              <small>
                                {product.sku ||
                                  product.code ||
                                  "Sin código"}
                              </small>
                            </span>

                            <b>
                              {money(product.sale_price)}
                            </b>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>

              {canManagePromotions && (
                <div className="promotion-form-actions">
                  <button
                    type="button"
                    className="btn-cancel-promotion"
                    onClick={startNewPromotion}
                    disabled={saving}
                  >
                    Limpiar
                  </button>

                  <button
                    type="submit"
                    className="btn-save-promotion"
                    disabled={saving}
                  >
                    {saving
                      ? "Guardando..."
                      : selectedPromotion
                        ? "Guardar cambios"
                        : "Crear promoción"}
                  </button>
                </div>
              )}
            </form>
          )}
        </section>
      </main>
    </div>
  );
}

export default CompanyPromotions;